import {
  Entity,
  PrimaryKey,
  Property,
  BeforeCreate,
  BeforeUpdate,
  Enum,
  OneToMany,
  Collection,
} from '@mikro-orm/core';
import { UserProgram } from './user-program.entity';
import { UserCategory } from './user-category.entity';

export enum UserStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  PENDING_VERIFICATION = 'pending_verification',
  SUSPENDED = 'suspended',
}

export enum SocialProvider {
  GOOGLE = 'google',
  FACEBOOK = 'facebook',
  TWITTER = 'twitter',
  GITHUB = 'github',
}

export enum UserRole {
  ADMIN = 'admin',
  USER = 'user',
}

@Entity({ tableName: 'users' })
export class User {
  @PrimaryKey()
  id: number;

  @Property()
  name: string;

  @Property()
  email: string;

  @Property({ length: 20 })
  phone?: string;

  @Property()
  password?: string;

  @Enum(() => UserStatus)
  status: UserStatus = UserStatus.PENDING_VERIFICATION;

  @Property()
  email_verification_token?: string | null;

  @Property()
  email_verified_at?: Date | null;

  @Property()
  password_reset_token?: string | null;

  @Property()
  password_reset_expires?: Date | null;

  // Social login fields
  @Enum(() => SocialProvider)
  social_provider?: SocialProvider;

  @Property()
  social_provider_id?: string;

  @Property()
  social_data?: object;

  @Property()
  last_login_at?: Date | null;

  @Property({ length: 45 })
  last_login_ip?: string | null;

  @Property()
  image_url?: string | null;

  @OneToMany(() => UserProgram, (up) => up.user)
  userPrograms = new Collection<UserProgram>(this);

  @OneToMany(() => UserCategory, (uc) => uc.user)
  userCategories = new Collection<UserCategory>(this);

  @Property()
  created_at: Date = new Date();

  @Property({ onUpdate: () => new Date() })
  updated_at: Date = new Date();

  @Property()
  deleted_at?: Date | null;

  @Enum(() => UserRole)
  role: UserRole = UserRole.USER;

  @BeforeCreate()
  @BeforeUpdate()
  updateTimestamps() {
    this.updated_at = new Date();
  }

  // Helper methods
  isEmailVerified(): boolean {
    return !!this.email_verified_at;
  }

  isActive(): boolean {
    return this.status === UserStatus.ACTIVE;
  }

  isSoftDeleted(): boolean {
    return !!this.deleted_at;
  }

  markAsDeleted(): void {
    this.deleted_at = new Date();
    this.status = UserStatus.INACTIVE;
  }

  markAsVerified(): void {
    this.email_verified_at = new Date();
    this.status = UserStatus.ACTIVE;
    this.email_verification_token = null;
  }
}
